import { post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";

import type {
  CreateProfessionalReferenceRequest,
  CreateProfessionalReferenceResponse,
} from "../types";
import {
  createProfessionalReferenceRequestSchema,
  createProfessionalReferenceResponseSchema,
} from "../utils/validation";
import { invalidateProfessionalReferences } from "./useGetProfessionalReferences";

const PROFESSIONAL_REFERENCES_URL = `${environmentConfig.REACT_APP_WORKER_SERVICE_URL}/references`;

export function useCreateProfessionalReference(
  options: UseMutationOptions<
    CreateProfessionalReferenceResponse,
    AxiosError<CreateProfessionalReferenceResponse>,
    CreateProfessionalReferenceRequest
  > = {}
): UseMutationResult<
  CreateProfessionalReferenceResponse,
  AxiosError<CreateProfessionalReferenceResponse>,
  CreateProfessionalReferenceRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await post({
        url: PROFESSIONAL_REFERENCES_URL,
        data,
        requestSchema: createProfessionalReferenceRequestSchema,
        responseSchema: createProfessionalReferenceResponseSchema,
      });

      return response.data;
    },
    onSuccess: async () => {
      // Invalidate professional references list
      await invalidateProfessionalReferences(queryClient);
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.CREATE_PROFESSIONAL_REFERENCE_FAILURE,
    },
    ...options,
  });
}
